// ============================================================================
// CENTRALIZED USER TYPES AND ROLE CONSTANTS
// ============================================================================
//
// PURPOSE: Single source of truth for all user types and role-related constants
//
// FEATURES:
// - Centralized role definitions
// - Type-safe role constants
// - Reusable role options for selects
// - Consistent role display names
// - Role validation utilities
//
// USAGE:
// - Import role constants instead of hardcoding strings
// - Use role options for select components
// - Validate roles with centralized functions
//
// ============================================================================

import { UserType } from "@/lib/types/database";

// ============================================================================
// ROLE CONSTANTS
// ============================================================================

export const USER_ROLES = {
  WTC_ADMIN: "wtc_admin",
  WTC_EMPLOYEE: "wtc_employee",
  WTC_CRM: "wtc_crm",
  CLIENT_USER: "client_user",
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// ============================================================================
// ROLE DISPLAY NAMES
// ============================================================================

export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  [USER_ROLES.WTC_ADMIN]: "Admin",
  [USER_ROLES.WTC_EMPLOYEE]: "Employee",
  [USER_ROLES.WTC_CRM]: "CRM",
  [USER_ROLES.CLIENT_USER]: "Client",
} as const;

// ============================================================================
// ROLE DESCRIPTIONS
// ============================================================================

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [USER_ROLES.WTC_ADMIN]:
    "Full system access - can see everything and manage all aspects of the system",
  [USER_ROLES.WTC_EMPLOYEE]: "Regular employees with access to contacts, tickets, and time entries",
  [USER_ROLES.WTC_CRM]:
    "CRM users with employee access plus CRM functions for selling and client management",
  [USER_ROLES.CLIENT_USER]: "External clients with limited access to their own tickets and data",
} as const;

// ============================================================================
// ROLE OPTIONS FOR SELECT COMPONENTS
// ============================================================================

export interface RoleOption {
  value: UserRole;
  label: string;
  description: string;
}

export const ROLE_OPTIONS: RoleOption[] = [
  {
    value: USER_ROLES.WTC_ADMIN,
    label: ROLE_DISPLAY_NAMES[USER_ROLES.WTC_ADMIN],
    description: ROLE_DESCRIPTIONS[USER_ROLES.WTC_ADMIN],
  },
  {
    value: USER_ROLES.WTC_EMPLOYEE,
    label: ROLE_DISPLAY_NAMES[USER_ROLES.WTC_EMPLOYEE],
    description: ROLE_DESCRIPTIONS[USER_ROLES.WTC_EMPLOYEE],
  },
  {
    value: USER_ROLES.WTC_CRM,
    label: ROLE_DISPLAY_NAMES[USER_ROLES.WTC_CRM],
    description: ROLE_DESCRIPTIONS[USER_ROLES.WTC_CRM],
  },
  {
    value: USER_ROLES.CLIENT_USER,
    label: ROLE_DISPLAY_NAMES[USER_ROLES.CLIENT_USER],
    description: ROLE_DESCRIPTIONS[USER_ROLES.CLIENT_USER],
  },
];

// ============================================================================
// ROLE VALIDATION UTILITIES
// ============================================================================

/**
 * Check if a string is a valid user role
 */
export function isValidUserRole(role: string): role is UserRole {
  return Object.values(USER_ROLES).includes(role as UserRole);
}

/**
 * Get role display name
 */
export function getRoleDisplayName(role: UserRole | string): string {
  return ROLE_DISPLAY_NAMES[role as UserRole] || role;
}

/**
 * Get role description
 */
export function getRoleDescription(role: UserRole | string): string {
  return ROLE_DESCRIPTIONS[role as UserRole] || "Unknown role";
}

/**
 * Resolve a user's role value from either the joined `roles` relation
 * (`role_id -> roles.name`, the canonical source) or the legacy scalar `role`
 * field. `app_users` no longer has a `role` column, so the role lives only in
 * the joined `roles.name` — reading `user.role` alone always yields undefined.
 */
export function resolveUserRole(
  user: { role?: string | null; roles?: { name?: string | null } | null } | null | undefined
): string | null {
  return user?.roles?.name || user?.role || null;
}

/**
 * Badge variant for a resolved role: admin = default, other employees =
 * secondary, clients/unknown = outline.
 */
export function getRoleBadgeVariant(
  role: string | null | undefined
): "default" | "secondary" | "outline" {
  if (!role) return "outline";
  if (isAdminRole(role)) return "default";
  if (isEmployeeRole(role)) return "secondary";
  return "outline";
}

// ============================================================================
// ROLE GROUPING UTILITIES
// ============================================================================

/**
 * Check if role is an employee role (includes admin, employee, and CRM)
 */
export function isEmployeeRole(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return [USER_ROLES.WTC_ADMIN, USER_ROLES.WTC_EMPLOYEE, USER_ROLES.WTC_CRM].includes(role as any);
}

/**
 * Check if role is an admin role
 */
export function isAdminRole(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === USER_ROLES.WTC_ADMIN;
}

/**
 * Check if role is a CRM role
 */
export function isCRMRole(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === USER_ROLES.WTC_CRM;
}

/**
 * Check if role is a client role
 */
export function isClientRole(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === USER_ROLES.CLIENT_USER;
}

// ============================================================================
// ROLE PERMISSIONS
// ============================================================================

export interface RolePermissions {
  can_manage_users: boolean;
  can_manage_contacts: boolean;
  can_manage_tickets: boolean;
  can_manage_time_entries: boolean;
  can_access_all_contacts: boolean;
  can_access_all_tickets: boolean;
  can_access_all_time_entries: boolean;
  can_manage_automation: boolean;
  can_manage_templates: boolean;
  can_access_reports: boolean;
  can_manage_quickbooks: boolean;
  can_manage_integrations: boolean;
  can_access_crm_functions: boolean;
  can_manage_proposals: boolean;
  can_manage_meetings: boolean;
  can_manage_tasks: boolean;
  can_view_own_tickets: boolean;
  can_view_own_time_entries: boolean;
  can_view_own_communications: boolean;
}

export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  [USER_ROLES.WTC_ADMIN]: {
    can_manage_users: true,
    can_manage_contacts: true,
    can_manage_tickets: true,
    can_manage_time_entries: true,
    can_access_all_contacts: true,
    can_access_all_tickets: true,
    can_access_all_time_entries: true,
    can_manage_automation: true,
    can_manage_templates: true,
    can_access_reports: true,
    can_manage_quickbooks: true,
    can_manage_integrations: true,
    can_access_crm_functions: true,
    can_manage_proposals: true,
    can_manage_meetings: true,
    can_manage_tasks: true,
    can_view_own_tickets: true,
    can_view_own_time_entries: true,
    can_view_own_communications: true,
  },
  [USER_ROLES.WTC_CRM]: {
    can_manage_users: false,
    can_manage_contacts: true,
    can_manage_tickets: true,
    can_manage_time_entries: true,
    can_access_all_contacts: false,
    can_access_all_tickets: false,
    can_access_all_time_entries: false,
    can_manage_automation: false,
    can_manage_templates: true,
    can_access_reports: true,
    can_manage_quickbooks: false,
    can_manage_integrations: false,
    can_access_crm_functions: true,
    can_manage_proposals: true,
    can_manage_meetings: true,
    can_manage_tasks: true,
    can_view_own_tickets: true,
    can_view_own_time_entries: true,
    can_view_own_communications: true,
  },
  [USER_ROLES.WTC_EMPLOYEE]: {
    can_manage_users: false,
    can_manage_contacts: true,
    can_manage_tickets: true,
    can_manage_time_entries: true,
    can_access_all_contacts: false,
    can_access_all_tickets: false,
    can_access_all_time_entries: false,
    can_manage_automation: false,
    can_manage_templates: true,
    can_access_reports: true,
    can_manage_quickbooks: false,
    can_manage_integrations: false,
    can_access_crm_functions: false,
    can_manage_proposals: false,
    can_manage_meetings: false,
    can_manage_tasks: false,
    can_view_own_tickets: true,
    can_view_own_time_entries: true,
    can_view_own_communications: true,
  },
  [USER_ROLES.CLIENT_USER]: {
    can_manage_users: false,
    can_manage_contacts: false,
    can_manage_tickets: false,
    can_manage_time_entries: false,
    can_access_all_contacts: false,
    can_access_all_tickets: false,
    can_access_all_time_entries: false,
    can_manage_automation: false,
    can_manage_templates: false,
    can_access_reports: false,
    can_manage_quickbooks: false,
    can_manage_integrations: false,
    can_access_crm_functions: false,
    can_manage_proposals: false,
    can_manage_meetings: false,
    can_manage_tasks: false,
    can_view_own_tickets: true,
    can_view_own_time_entries: true,
    can_view_own_communications: true,
  },
};

/**
 * Get permissions for a specific role
 */
export function getRolePermissions(role: UserRole | string): RolePermissions {
  return ROLE_PERMISSIONS[role as UserRole] || ROLE_PERMISSIONS[USER_ROLES.CLIENT_USER];
}

/**
 * Check if a role has a specific permission
 */
export function hasRolePermission(
  role: UserRole | string,
  permission: keyof RolePermissions
): boolean {
  const permissions = getRolePermissions(role);
  return permissions[permission] === true;
}
